import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Input,
  Select,
  Row,
  Col,
  Button,
  DatePicker,
  Modal,
  Descriptions,
  Statistic,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  WarningOutlined,
  BellOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CarOutlined,
} from "@ant-design/icons";
import { getNotifiedInfractions } from "../../api/parkpass/parkpassNotifiedInfractions";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColors = {
  paid: "green",
  pending: "orange",
  canceled: "red",
};

const statusLabels = {
  paid: "Payée",
  pending: "Non payée",
  canceled: "Annulée",
};

const notificationStatusColors = {
  true: "green",
  false: "red",
};

const CitizenNotifiedInfractionsPage = () => {
  const [infractions, setInfractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedInfraction, setSelectedInfraction] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    userNotified: undefined,
    notifiedFrom: undefined,
    notifiedTo: undefined,
    matricule: undefined,
    status: undefined,
  });

  useEffect(() => {
    fetchInfractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInfractions = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    currentFilters = filters
  ) => {
    try {
      setLoading(true);
      const response = await getNotifiedInfractions({
        page,
        pageSize,
        userNotified: currentFilters.userNotified,
        notifiedFrom: currentFilters.notifiedFrom,
        notifiedTo: currentFilters.notifiedTo,
        matricule: currentFilters.matricule,
        status: currentFilters.status,
      });
      setInfractions(response.items || []);
      setPagination({
        current: response.page,
        pageSize: response.page_size,
        total: response.total,
        totalPages: response.total_pages,
      });
    } catch (error) {
      console.error("Error fetching notified infractions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchInfractions(newPagination.current, newPagination.pageSize, filters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setFilters((prev) => ({
        ...prev,
        notifiedFrom: dates[0].toISOString(),
        notifiedTo: dates[1].toISOString(),
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        notifiedFrom: undefined,
        notifiedTo: undefined,
      }));
    }
  };

  const handleSearch = () => {
    fetchInfractions(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const resetFilters = {
      userNotified: undefined,
      notifiedFrom: undefined,
      notifiedTo: undefined,
      matricule: undefined,
      status: undefined,
    };
    setFilters(resetFilters);
    fetchInfractions(1, pagination.pageSize, resetFilters);
  };

  const handleViewDetail = (record) => {
    setSelectedInfraction(record);
    setDetailModalVisible(true);
  };

  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedInfraction(null);
  };

  // Statistics from current page data
  const notifiedCount = infractions.filter((i) => i.user_notified === true).length;
  const notNotifiedCount = infractions.filter((i) => i.user_notified === false).length;

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "Référence",
      dataIndex: "reference",
      key: "reference",
      width: 140,
      render: (ref) => (
        <Text strong style={{ fontFamily: "monospace" }}>
          {ref}
        </Text>
      ),
    },
    {
      title: "Matricule",
      dataIndex: "matricule",
      key: "matricule",
      width: 130,
      render: (plate) => (
        <Tag color="blue" icon={<CarOutlined />}>
          {plate}
        </Tag>
      ),
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>
          {amount} MAD
        </span>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => (
        <Tag color={statusColors[status] || "default"}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: "Notifié",
      dataIndex: "user_notified",
      key: "user_notified",
      width: 110,
      align: "center",
      render: (notified) =>
        notified ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Oui
          </Tag>
        ) : (
          <Tag color="red" icon={<CloseCircleOutlined />}>
            Non
          </Tag>
        ),
    },
    {
      title: "Date de notification",
      dataIndex: "notified_at",
      key: "notified_at",
      width: 180,
      render: (date) =>
        date ? (
          new Date(date).toLocaleString("fr-FR")
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Date d'infraction",
      dataIndex: "date_infraction",
      key: "date_infraction",
      width: 180,
      render: (date) =>
        date ? new Date(date).toLocaleString("fr-FR") : <Text type="secondary">—</Text>,
    },
    {
      title: "Marque",
      dataIndex: "brand",
      key: "brand",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Modèle",
      dataIndex: "model",
      key: "model",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewDetail(record)}
        >
          Détails
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Infractions"
              value={pagination.total}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Notifiés (page courante)"
              value={notifiedCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Non notifiés (page courante)"
              value={notNotifiedCount}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <BellOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Infractions Notifiées - Citizen
            </Title>
          </Space>
        }
      >
        {/* Filters */}
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={4}>
              <Input
                placeholder="Rechercher par matricule"
                prefix={<SearchOutlined />}
                value={filters.matricule}
                onChange={(e) =>
                  handleFilterChange("matricule", e.target.value || undefined)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Statut de notification"
                style={{ width: "100%" }}
                value={filters.userNotified}
                onChange={(value) => handleFilterChange("userNotified", value)}
                allowClear
              >
                <Option value={true}>
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    Notifié
                  </Tag>
                </Option>
                <Option value={false}>
                  <Tag color="red" icon={<CloseCircleOutlined />}>
                    Non notifié
                  </Tag>
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Statut infraction"
                style={{ width: "100%" }}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                allowClear
              >
                <Option value="paid">
                  <Tag color={statusColors.paid}>{statusLabels.paid}</Tag>
                </Option>
                <Option value="pending">
                  <Tag color={statusColors.pending}>{statusLabels.pending}</Tag>
                </Option>
                <Option value="canceled">
                  <Tag color={statusColors.canceled}>{statusLabels.canceled}</Tag>
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={7}>
              <RangePicker
                style={{ width: "100%" }}
                showTime
                format="YYYY-MM-DD HH:mm"
                placeholder={["Notifié depuis", "Notifié jusqu'à"]}
                onChange={handleDateRangeChange}
                value={
                  filters.notifiedFrom && filters.notifiedTo
                    ? [dayjs(filters.notifiedFrom), dayjs(filters.notifiedTo)]
                    : null
                }
              />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Rechercher
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Réinitialiser
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={infractions}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} infractions`,
            pageSizeOptions: ["10", "25", "50", "100"],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            <span>Détails de l'infraction</span>
          </Space>
        }
        open={detailModalVisible}
        onCancel={handleCloseDetail}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseDetail}>
            Fermer
          </Button>,
        ]}
        width={800}
      >
        {selectedInfraction && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID">
              <strong>{selectedInfraction.id}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Référence">
              <Text strong style={{ fontFamily: "monospace" }}>
                {selectedInfraction.reference}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Matricule">
              <Tag color="blue" icon={<CarOutlined />}>
                {selectedInfraction.matricule}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Marque">
              {selectedInfraction.brand || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Modèle">
              {selectedInfraction.model || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Montant">
              <span style={{ fontWeight: 600, fontSize: 16, color: "#52c41a" }}>
                {selectedInfraction.amount} MAD
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Statut">
              <Tag color={statusColors[selectedInfraction.status] || "default"}>
                {statusLabels[selectedInfraction.status] || selectedInfraction.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Utilisateur notifié">
              {selectedInfraction.user_notified ? (
                <Tag color="green" icon={<CheckCircleOutlined />}>
                  Oui
                </Tag>
              ) : (
                <Tag color="red" icon={<CloseCircleOutlined />}>
                  Non
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Date de notification">
              {selectedInfraction.notified_at
                ? new Date(selectedInfraction.notified_at).toLocaleString("fr-FR", {
                    dateStyle: "full",
                    timeStyle: "long",
                  })
                : "Non notifié"}
            </Descriptions.Item>
            <Descriptions.Item label="Date d'infraction">
              {selectedInfraction.date_infraction
                ? new Date(selectedInfraction.date_infraction).toLocaleString("fr-FR", {
                    dateStyle: "full",
                    timeStyle: "long",
                  })
                : "N/A"}
            </Descriptions.Item>
            {selectedInfraction.type_infraction && (
              <Descriptions.Item label="Type d'infraction">
                {selectedInfraction.type_infraction.name || "N/A"}
              </Descriptions.Item>
            )}
            {selectedInfraction.zone && (
              <Descriptions.Item label="Zone">
                {selectedInfraction.zone.name || "N/A"}
              </Descriptions.Item>
            )}
            {selectedInfraction.section && (
              <Descriptions.Item label="Section">
                {selectedInfraction.section.name || "N/A"}
              </Descriptions.Item>
            )}
            {selectedInfraction.street && (
              <Descriptions.Item label="Rue">
                {selectedInfraction.street.name || "N/A"}
              </Descriptions.Item>
            )}
            {selectedInfraction.line && (
              <Descriptions.Item label="Ligne">
                {selectedInfraction.line.name || "N/A"}
              </Descriptions.Item>
            )}
            {selectedInfraction.user && (
              <Descriptions.Item label="Agent">
                {selectedInfraction.user.full_name || "N/A"}
              </Descriptions.Item>
            )}
            {selectedInfraction.created_at && (
              <Descriptions.Item label="Date de création">
                {new Date(selectedInfraction.created_at).toLocaleString("fr-FR", {
                  dateStyle: "full",
                  timeStyle: "long",
                })}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CitizenNotifiedInfractionsPage;
